import { z } from 'zod';

/**
 * Massenänderungen ohne KI (PIM G3, Briefing Abschnitt 4): Aktionen, Auswahl, Läufe. Die Aktionen sind reine Funktionen –
 * dieselben für Vorschau und Lauf. Ziele: Titel, Beschreibung, Marke und Produktfelder; Kategorie über die eigene Aktion.
 */

export const BULK_FELDER_STAMM = ['titel', 'beschreibung', 'marke'] as const;
const Feld = z.string().max(140).refine((f) => (BULK_FELDER_STAMM as readonly string[]).includes(f) || /^felder\.[^.]+\.[^.]+$/.test(f), 'Unbekanntes Feld');

export const BulkAktionSchema = z.discriminatedUnion('art', [
  z.object({ art: z.literal('setzen'), feld: Feld, wert: z.string().max(10_000) }),
  z.object({ art: z.literal('ergaenzen'), feld: Feld, text: z.string().min(1).max(1000), wo: z.enum(['vorne', 'hinten']) }),
  z.object({ art: z.literal('leeren'), feld: Feld }),
  z.object({
    art: z.literal('ersetzen'), feld: Feld, suchen: z.string().min(1).max(500), ersetzen: z.string().max(1000),
    regex: z.boolean().default(false), grossKlein: z.boolean().default(false),
  }),
  z.object({ art: z.literal('schreibweise'), feld: Feld, schreibweise: z.enum(['gross', 'klein', 'woerter']) }),
  /** Vorlage aus anderen Werten: {titel}, {marke}, {kategorie}, {felder.ns.key} */
  z.object({ art: z.literal('vorlage'), feld: Feld, vorlage: z.string().min(1).max(1000) }),
  /** Einheiten umrechnen: Zahl × Faktor, gerundet */
  z.object({ art: z.literal('umrechnen'), feld: Feld, faktor: z.number().positive().max(1_000_000), nachkomma: z.number().int().min(0).max(6).default(2) }),
  /** Händlerwerte auf Auswahlwerte abbilden („dunkelblau“, „navy“ → „Blau“), ohne Groß-/Kleinschreibung */
  z.object({ art: z.literal('abbilden'), feld: Feld, zuordnung: z.record(z.string().max(200), z.string().max(200)).refine((r) => Object.keys(r).length > 0 && Object.keys(r).length <= 500, 'Bitte 1–500 Zuordnungen angeben') }),
  z.object({ art: z.literal('kategorie'), kategorie: z.string().max(255) }),
]);
export type BulkAktion = z.infer<typeof BulkAktionSchema>;
export type BulkAktionEingabe = z.input<typeof BulkAktionSchema>;

/** Filter wie in der Produktliste (GET /api/konto/produkte) */
export const BulkFilterSchema = z.object({
  q: z.string().trim().max(100).optional(),
  status: z.string().max(20).optional(),
  haendler: z.string().uuid().optional(),
  kategorie: z.string().max(120).optional(),
  marke: z.string().max(120).optional(),
  maengel: z.boolean().optional(),
  vollstaendigkeitBis: z.number().int().min(0).max(100).optional(),
  geaendertSeit: z.string().datetime({ offset: true }).optional(),
});
export type BulkFilter = z.infer<typeof BulkFilterSchema>;

/** Auswahl: einzelne Produkte oder „alle Treffer des Filters“ */
export const BulkAuswahlSchema = z.union([
  z.object({ ids: z.array(z.string().uuid()).min(1).max(10_000) }),
  z.object({ filter: BulkFilterSchema }),
]);
export type BulkAuswahl = z.infer<typeof BulkAuswahlSchema>;

export const BulkSchema = z.object({ auswahl: BulkAuswahlSchema, aktion: BulkAktionSchema });
export type BulkEingabe = z.input<typeof BulkSchema>;

export interface BulkVorschauZeile { id: string; titel: string; feld: string; alt: string; neu: string; fehler?: string }
export interface BulkVorschau {
  /** Produkte der Auswahl (ohne Eigenprodukte); bei vielen begrenzt gezählt */
  anzahl: number;
  anzahlGenau: boolean;
  /** Stichprobe der ersten Änderungen */
  stichprobe: BulkVorschauZeile[];
  /** geprüfte Produkte für die Stichprobe und wie viele davon sich ändern würden */
  geprueft: number;
  aendern: number;
}

export const PRODUKT_LAUF_ARTEN = ['bulk', 'excel', 'rueckgaengig'] as const;
export const PRODUKT_LAUF_STATUS = ['vorschau', 'laeuft', 'fertig', 'fehler', 'abgebrochen'] as const;
export type ProduktLaufArt = (typeof PRODUKT_LAUF_ARTEN)[number];
export type ProduktLaufStatus = (typeof PRODUKT_LAUF_STATUS)[number];

export interface ProduktLauf {
  id: string;
  art: ProduktLaufArt;
  status: ProduktLaufStatus;
  /** kurze Beschreibung, z. B. „Titel: ersetzen „ Herren“ → „““ */
  titel: string;
  gesamt: number;
  bearbeitet: number;
  geaendert: number;
  /** abgelehnte Werte (Validierung) bzw. beim Rückgängig übersprungene, weil inzwischen geändert */
  fehler: number;
  fehlerBeispiele: { id: string; feld: string; grund: string }[];
  nutzer: string | null;
  erstelltAm: string;
  fertigAm: string | null;
  /** kann als Ganzes rückgängig gemacht werden (fertig, nicht schon zurückgenommen) */
  rueckgaengigMoeglich: boolean;
  /** Lauf, der diesen zurückgenommen hat bzw. den dieser zurücknimmt */
  bezug: string | null;
}

/* ---------------------------------------------------------------- Aktionen anwenden (rein) */

export interface BulkProdukt { titel: string; beschreibung: string; marke: string; kategorie: string; felder: Record<string, string> }

const lesen = (p: BulkProdukt, feld: string): string =>
  feld.startsWith('felder.') ? (p.felder[feld.slice(7)] ?? '') : String((p as unknown as Record<string, string>)[feld] ?? '');

const wortweise = (s: string) => s.toLocaleLowerCase('de-DE').replace(/(^|[\s\-/(])(\p{L})/gu, (_, a: string, b: string) => a + b.toLocaleUpperCase('de-DE'));
const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Zahl aus einem Wert („12,5 cm“ → 12.5), null wenn keine */
function zahlAus(s: string): number | null {
  const m = /-?\d+(?:[.,]\d+)?/.exec(s.replace(/\s/g, ''));
  return m ? Number(m[0].replace(',', '.')) : null;
}

/**
 * Neuer Wert eines Produkts für die Aktion – null, wenn sich nichts ändert; { fehler } bei ungültiger Eingabe
 * (z. B. fehlerhafter regulärer Ausdruck). Kategorie liefert feld „kategorie“.
 */
export function bulkAnwenden(a: BulkAktion, p: BulkProdukt): { feld: string; alt: string; neu: string } | { fehler: string } | null {
  if (a.art === 'kategorie') {
    return p.kategorie === a.kategorie ? null : { feld: 'kategorie', alt: p.kategorie, neu: a.kategorie };
  }
  const alt = lesen(p, a.feld);
  let neu = alt;
  switch (a.art) {
    case 'setzen': neu = a.wert; break;
    case 'ergaenzen': neu = !alt ? a.text : a.wo === 'vorne' ? `${a.text}${alt}` : `${alt}${a.text}`; break;
    case 'leeren': neu = ''; break;
    case 'ersetzen': {
      let re: RegExp;
      try {
        re = new RegExp(a.regex ? a.suchen : escape(a.suchen), a.grossKlein ? 'g' : 'gi');
      } catch {
        return { fehler: 'Ungültiger regulärer Ausdruck' };
      }
      neu = alt.replace(re, a.ersetzen);
      break;
    }
    case 'schreibweise':
      neu = a.schreibweise === 'gross' ? alt.toLocaleUpperCase('de-DE') : a.schreibweise === 'klein' ? alt.toLocaleLowerCase('de-DE') : wortweise(alt);
      break;
    case 'vorlage':
      neu = a.vorlage.replace(/\{([^{}]+)\}/g, (_, k: string) => lesen(p, k.trim())).replace(/\s+/g, ' ').replace(/\s+([–,-])\s*$/u, '').trim();
      break;
    case 'umrechnen': {
      const n = zahlAus(alt);
      if (n === null) return null;
      neu = String(Number((n * a.faktor).toFixed(a.nachkomma)));
      break;
    }
    case 'abbilden': {
      const z = new Map(Object.entries(a.zuordnung).map(([k, v]) => [k.trim().toLocaleLowerCase('de-DE'), v]));
      const ziel = z.get(alt.trim().toLocaleLowerCase('de-DE'));
      if (ziel === undefined) return null;
      neu = ziel;
      break;
    }
  }
  if (a.feld === 'titel') neu = neu.replace(/\s+/g, ' ').trim();
  return neu === alt ? null : { feld: a.feld, alt, neu };
}

/** Kurztext einer Aktion für Laufliste und Protokoll */
export function bulkTitel(a: BulkAktion, feldName: (f: string) => string = (f) => f): string {
  if (a.art === 'kategorie') return a.kategorie ? `Kategorie „${a.kategorie}“ zuordnen` : 'Kategorie entfernen';
  const f = feldName(a.feld);
  switch (a.art) {
    case 'setzen': return `${f}: auf „${a.wert.slice(0, 40)}“ setzen`;
    case 'ergaenzen': return `${f}: „${a.text.slice(0, 40)}“ ${a.wo === 'vorne' ? 'voranstellen' : 'anhängen'}`;
    case 'leeren': return `${f}: leeren`;
    case 'ersetzen': return `${f}: „${a.suchen.slice(0, 30)}“ durch „${a.ersetzen.slice(0, 30)}“ ersetzen`;
    case 'schreibweise': return `${f}: ${a.schreibweise === 'gross' ? 'GROSS' : a.schreibweise === 'klein' ? 'klein' : 'Wörter Groß'} schreiben`;
    case 'vorlage': return `${f}: aus Vorlage „${a.vorlage.slice(0, 40)}“`;
    case 'umrechnen': return `${f}: × ${a.faktor.toLocaleString('de-DE')} umrechnen`;
    case 'abbilden': return `${f}: ${Object.keys(a.zuordnung).length} Werte abbilden`;
  }
}

/** Excel-Rundlauf: Vorschau nach dem Hochladen (übernehmen mit POST /api/konto/produkte/laeufe/:laufId/starten) */
export interface ExcelVorschau {
  laufId: string;
  /** gelesene Zeilen ohne Kopf */
  zeilen: number;
  produkte: number;
  /** gültige geänderte Zellen */
  aenderungen: number;
  beispiele: { id: string; titel: string; feld: string; alt: string; neu: string }[];
  fehler: { id: string; feld: string; grund: string }[];
  fehlerAnzahl: number;
  /** so viele Änderungen sind schon probeweise geprüft; die übrigen prüft der Lauf */
  geprueft: number;
  unbekannteSpalten: string[];
}
